import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../styles/pages/PagoPubPage.css';

const PagoPreviredPubPage = () => {
  const { codigo } = useParams();
  const [pago, setPago] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comprobante, setComprobante] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    const fetchPago = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/pago-previred/${codigo}`);
        setPago(res.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.error || 'Error al cargar datos del pago');
        setLoading(false);
      }
    };
    fetchPago();
  }, [codigo]);

  const formatMonto = (monto) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(monto);

  const getNombreBanco = (codigo) => {
    const bancos = { banco_estado: 'Banco Estado', itau: 'Banco Itaú', scotiabank: 'Scotiabank' };
    return bancos[codigo] || codigo;
  };

  const getNombreMes = (mes) => {
    const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                   'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    return meses[mes - 1] || mes;
  };

  const copiarDatos = () => {
    if (!pago) return;
    const datos = `
Pago Previred - ${getNombreMes(pago.mes)} ${pago.anual}
Empresa: ${pago.razon_social} (${pago.rut_empresa})
Monto a pagar: ${formatMonto(pago.monto)}

Datos bancarios:
Banco: ${getNombreBanco(pago.cuenta_bancaria.banco)}
Tipo de cuenta: ${pago.cuenta_bancaria.tipo_cuenta}
Número de cuenta: ${pago.cuenta_bancaria.numero_cuenta}
RUT titular: ${pago.cuenta_bancaria.rut_titular}
Nombre titular: ${pago.cuenta_bancaria.nombre_titular}

Glosa/Referencia: Pago Previred ${getNombreMes(pago.mes)} ${pago.anual} - ${pago.razon_social}
    `.trim();
    navigator.clipboard.writeText(datos);
    alert('Datos copiados al portapapeles');
  };

  const handleSubirComprobante = async (e) => {
    e.preventDefault();
    if (!comprobante) { alert('Debe seleccionar un archivo'); return; }

    const formData = new FormData();
    formData.append('comprobante', comprobante);
    formData.append('codigo_pago', codigo);

    try {
      setUploading(true);
      const response = await axios.post(`${API_BASE_URL}/api/subir-comprobante-previred`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPago(prev => ({
        ...prev,
        comprobante_url: response.data.comprobante_url,
        tiene_comprobante: true,
        estado: 'comprobante_subido',
      }));
      setUploadSuccess(true);
      setUploading(false);
      alert('Comprobante subido exitosamente. El pago será verificado pronto.');
    } catch (err) {
      setUploading(false);
      alert(err.response?.data?.error || 'Error al subir comprobante');
    }
  };

  if (loading) {
    return (
      <div className="pago-pub-page">
        <div className="pago-container"><p>Cargando datos del pago...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pago-pub-page">
        <div className="pago-container error"><h2>Error</h2><p>{error}</p></div>
      </div>
    );
  }

  if (!pago) {
    return (
      <div className="pago-pub-page">
        <div className="pago-container error"><h2>Pago no encontrado</h2></div>
      </div>
    );
  }

  const estadoPagado = pago.estado === 'confirmado';

  return (
    <div className="pago-pub-page">
      <div className="pago-container">
        <div className="pago-header">
          <h1>Pago Previred</h1>
          <p style={{ color: '#666', margin: '5px 0 0' }}>Cotizaciones previsionales y de salud</p>
          {estadoPagado && (
            <div className="estado-pagado"><span>✅ PAGADO</span></div>
          )}
        </div>

        <div className="pago-info">
          <div className="info-row">
            <strong>Período:</strong>
            <span>{getNombreMes(pago.mes)} {pago.anual}</span>
          </div>
          <div className="info-row">
            <strong>Empresa:</strong>
            <span>{pago.razon_social}</span>
          </div>
          <div className="info-row">
            <strong>RUT:</strong>
            <span>{pago.rut_empresa}</span>
          </div>
          <div className="info-row monto">
            <strong>Monto a pagar:</strong>
            <span className="monto-valor">{formatMonto(pago.monto)}</span>
          </div>
        </div>

        {!estadoPagado && (
          <>
            <div className="instrucciones">
              <h3>Instrucciones de pago</h3>
              <ol>
                <li>Ingresa a tu aplicación bancaria</li>
                <li>Realiza una transferencia con los siguientes datos</li>
                <li>Sube el comprobante de pago más abajo para que tu contador procese el pago en Previred</li>
              </ol>
            </div>

            <div className="datos-bancarios">
              <h3>Datos bancarios</h3>
              <div className="datos-grid">
                <div className="dato-item">
                  <strong>Banco:</strong>
                  <span>{getNombreBanco(pago.cuenta_bancaria.banco)}</span>
                </div>
                <div className="dato-item">
                  <strong>Tipo de cuenta:</strong>
                  <span>{pago.cuenta_bancaria.tipo_cuenta}</span>
                </div>
                <div className="dato-item">
                  <strong>Número de cuenta:</strong>
                  <span className="dato-destacado">{pago.cuenta_bancaria.numero_cuenta}</span>
                </div>
                <div className="dato-item">
                  <strong>RUT titular:</strong>
                  <span>{pago.cuenta_bancaria.rut_titular}</span>
                </div>
                <div className="dato-item">
                  <strong>Nombre titular:</strong>
                  <span>{pago.cuenta_bancaria.nombre_titular}</span>
                </div>
                <div className="dato-item full-width">
                  <strong>Glosa/Referencia:</strong>
                  <span>Pago Previred {getNombreMes(pago.mes)} {pago.anual} - {pago.razon_social}</span>
                </div>
              </div>
            </div>

            <div className="acciones">
              <button className="btn-primary" onClick={copiarDatos}>
                📋 Copiar todos los datos
              </button>
            </div>

            <div className="subir-comprobante">
              <h3>Subir comprobante de transferencia</h3>
              {pago.tiene_comprobante || uploadSuccess ? (
                <div className="upload-success">
                  <p>✅ Comprobante subido exitosamente</p>
                  {pago.estado === 'comprobante_subido' && (
                    <p>Estado: <strong>Pendiente de validación</strong></p>
                  )}
                  {pago.comprobante_url && (
                    <p>
                      <a href={pago.comprobante_url} target="_blank" rel="noopener noreferrer" className="btn-link">
                        📄 Ver comprobante
                      </a>
                    </p>
                  )}
                  {uploadSuccess && <p>Tu contador procesará el pago en Previred una vez verificado.</p>}
                </div>
              ) : (
                <form onSubmit={handleSubirComprobante}>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setComprobante(e.target.files[0])}
                    disabled={uploading}
                  />
                  <button type="submit" disabled={uploading || !comprobante}>
                    {uploading ? 'Subiendo...' : 'Subir comprobante'}
                  </button>
                </form>
              )}
            </div>
          </>
        )}

        {estadoPagado && (
          <div className="pago-confirmado-mensaje">
            <h3>Pago confirmado</h3>
            <p>Este pago ya ha sido confirmado y procesado por tu contador.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PagoPreviredPubPage;
